import { useCallback, useMemo } from 'react';

import { AgGridServerTable } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider, SelectionCountProvider } from 'core/states';
import { AgGridGetRowsParams, ISelectionActionProps, ITableActionProps } from 'core/types';
import {
  RemovePricingProfileAdminUserButton,
  UpsertPricingProfileAdminUserButton,
} from 'pricing/components';
import { PricingProfileAdminUserRole } from 'pricing/enums';
import { adminUserService } from 'pricing/services';
import { IPricingProfile, IPricingProfileAdminUser } from 'pricing/types';

import { getColumnDef } from './columns';

interface IProps {
  pricingProfile: IPricingProfile;
  isReadOnly?: boolean;
}

export const AdminUserList = ({ isReadOnly = false, pricingProfile }: IProps) => {
  const columnDefs = useMemo(() => getColumnDef({ isEditable: !isReadOnly }), [isReadOnly]);

  const actionButtons = useCallback(
    ({ gridApi }: ITableActionProps) => {
      if (isReadOnly) {
        return [];
      }

      return [
        <UpsertPricingProfileAdminUserButton
          key="upsertPricingProfileAdminUserButton"
          pricingProfileId={pricingProfile.id}
          onSubmitSuccess={() => gridApi?.refreshServerSide()}
        />,
      ];
    },
    [isReadOnly, pricingProfile.id]
  );

  const selectionActionButtons = useCallback(
    ({ gridApi, selectedRows, deselectAllRows }: ISelectionActionProps) => {
      if (isReadOnly) {
        return [];
      }

      return [
        <RemovePricingProfileAdminUserButton
          key="removeSelectedAdminUsersButton"
          pricingProfileAdminUsers={selectedRows as IPricingProfileAdminUser[]}
          onRemoveSuccess={() => {
            deselectAllRows();
            gridApi?.refreshServerSide();
          }}
        />,
      ];
    },
    [isReadOnly]
  );

  const getRows = useCallback(
    ({ filters: { role, ...restFilters }, ...rest }: AgGridGetRowsParams) => {
      const filters: Record<string, unknown> = {
        ...restFilters,
      };

      if (Array.isArray(role) && role.length === 1) {
        filters.isOwner = role[0] === PricingProfileAdminUserRole.OWNER ? 1 : 0;
      }

      return adminUserService.getPricingProfileAdminUsers({
        filters: {
          ...filters,
          pricingProfileId: pricingProfile.id,
        },
        ...rest,
      });
    },
    [pricingProfile.id]
  );

  const isRowSelectable = useCallback(
    (params: { data: IPricingProfileAdminUser }) => {
      if (isReadOnly) {
        return false;
      }

      return !params.data?.isOwner;
    },
    [isReadOnly]
  );

  return (
    <SelectionCountProvider>
      <AgGridServerTableProvider
        tableId="pricingProfileAdminUsersTable"
        entityType="AdminUser"
        columnDefs={columnDefs}
        getRows={getRows}
        hasLayoutActions
        hasManageFilters
        layoutType={TableLayoutType.PRICING_PROFILE_ADMIN_USERS}
        isRowSelectable={isRowSelectable}
      >
        <AgGridServerTable
          actionButtons={actionButtons}
          selectionActionButtons={selectionActionButtons}
        />
      </AgGridServerTableProvider>
    </SelectionCountProvider>
  );
};
